import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Link,
  Chip,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import axios from '../utils/axios';
import { translations } from './publicRegistrationTranslations';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const PublicRegistration = () => {
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Language toggle — display-only. Submitted form values stay in English so
  // the API and management page are unaffected by this choice.
  const [language, setLanguage] = useState(() => {
    try {
      const saved = window.localStorage.getItem('reRegistrationLanguage');
      return saved === 'es' ? 'es' : 'en';
    } catch {
      return 'en';
    }
  });
  const t = translations[language];
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    try { window.localStorage.setItem('reRegistrationLanguage', lang); } catch {}
  };

  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // One payment idempotency key per logical submission: kept across retries so
  // a resubmit after an ambiguous failure can never charge the card twice.
  const paymentIdemKeyRef = useRef(null);

  // Settings and programs
  const [settings, setSettings] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);

  // Returning family state
  const [isReturningFamily, setIsReturningFamily] = useState(null);
  const [returningEmail, setReturningEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState({ text: '', type: '' });
  const [prefilledData, setPrefilledData] = useState(null);

  // Parish registration
  const [parishRegistered, setParishRegistered] = useState(null);

  // Family information
  const [parents, setParents] = useState([
    { name: '', relationship: '', maidenName: '' }
  ]);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // Relationship options for parents/guardians
  const relationshipOptions = [
    'Father', 'Mother', 'Grandparent', 'Guardian', 'Other'
  ];

  // Children
  const [children, setChildren] = useState([createEmptyChild(1)]);
  const [currentChildIndex, setCurrentChildIndex] = useState(0);

  // Permissions
  const [photoPermission, setPhotoPermission] = useState(null);

  // Signature
  const [signature, setSignature] = useState('');
  const [signatureDate, setSignatureDate] = useState('');

  // Payment
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [squareCard, setSquareCard] = useState(null);
  const [squarePayments, setSquarePayments] = useState(null);

  // File uploads
  const [certificateFiles, setCertificateFiles] = useState({});
  // Per-child acknowledgment when no baptismal certificate is uploaded — required
  // only when the child meets the certificate-needed conditions and no file is provided.
  // Not persisted to the server; purely a client-side gate.
  const [certificateAcknowledgments, setCertificateAcknowledgments] = useState({});

  const steps = t.steps;

  // Track if previous registration was paid in full (for sacrament fee logic)
  const [previousRegistrationPaidInFull, setPreviousRegistrationPaidInFull] = useState(null);

  function createEmptyChild(num) {
    return {
      id: num,
      name: '',
      dateOfBirth: '',
      grade: '',
      catholicSchool: null,
      schoolName: '',
      sacramentsReceived: [],
      sacramentsPreparing: [],
      previouslyPreparing: [], // Track what sacraments they were preparing last year
      alreadyPaidSacramentFee: null, // For returning families with unpaid balance
      baptizedAtStBridget: null,
      previouslyEnrolled: null,
      certificateFile: null,
      program: []
    };
  }

  // Fetch settings and programs on load
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch settings
        const settingsRes = await axios.get('/religious-education/settings/status-public');
        setSettings(settingsRes.data);
        setRegistrationEnabled(settingsRes.data.registrationEnabled);

        // Fetch programs
        try {
          const programsRes = await axios.get('/programs/current');
          setPrograms(programsRes.data.programs || []);
        } catch (e) {
          console.error('Error fetching programs:', e);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching settings:', err);
        setError('Unable to load registration form. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Initialize Square when on payment step
  useEffect(() => {
    if (activeStep === 6 && paymentMethod === 'online' && !squarePayments) {
      initializeSquare();
    }
  }, [activeStep, paymentMethod]);

  const initializeSquare = async () => {
    try {
      // Pull applicationId/locationId from the server so dev (sandbox) and prod
      // both work without changing client code. Same pattern as PaymentLookup.
      const configRes = await axios.get('/square-config');
      const { applicationId, locationId } = configRes.data || {};
      if (!applicationId || !locationId) {
        throw new Error('Square configuration is incomplete.');
      }

      const isProd = window.location.hostname.includes('stbridgetup.org');
      const sdkUrl = isProd
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js';

      const ensureSdk = () => new Promise((resolve, reject) => {
        if (window.Square) return resolve();
        const existing = document.querySelector(`script[src="${sdkUrl}"]`);
        if (existing) {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', reject);
          return;
        }
        const script = document.createElement('script');
        script.src = sdkUrl;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      });

      await ensureSdk();
      await initSquarePayments(applicationId, locationId);
    } catch (err) {
      console.error('Error initializing Square:', err);
      setError(t.paymentFailed);
    }
  };

  const initSquarePayments = async (appId, locationId) => {
    try {
      const payments = window.Square.payments(appId, locationId);
      setSquarePayments(payments);

      const card = await payments.card();
      await card.attach('#card-container');
      setSquareCard(card);
    } catch (err) {
      console.error('Error initializing Square payments:', err);
      setError(t.paymentFailed);
    }
  };

  // Handle returning family verification
  const handleSendVerificationCode = async () => {
    if (!returningEmail) {
      setVerificationMessage({ text: t.pleaseEnterEmail, type: 'error' });
      return;
    }

    try {
      await axios.post('/religious-education/family-lookup', { email: returningEmail });
      setVerificationSent(true);
      setVerificationMessage({ text: t.codeSent, type: 'success' });
    } catch (err) {
      setVerificationMessage({ text: t.codeSendFailed, type: 'error' });
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setVerificationMessage({ text: t.pleaseEnterCode, type: 'error' });
      return;
    }

    try {
      const res = await axios.post('/religious-education/family-verify', {
        email: returningEmail,
        code: verificationCode
      });

      if (res.data.prefilledData) {
        applyPrefilledData(res.data.prefilledData);
        setVerificationMessage({ text: t.infoLoaded, type: 'success' });
      }
    } catch (err) {
      setVerificationMessage({ text: err.response?.data?.message || t.invalidCode, type: 'error' });
    }
  };

  const applyPrefilledData = (data) => {
    setPrefilledData(data);

    if (data.family) {
      // Handle new parents data if available
      if (data.family.parents && data.family.parents.length > 0) {
        setParents(data.family.parents.map(p => ({
          name: p.name || '',
          relationship: p.relationship || '',
          maidenName: p.maidenName || ''
        })));
      }

      if (data.family.primaryContact) {
        setContactName(data.family.primaryContact.name || '');
        setContactEmail(data.family.primaryContact.email || '');
        setContactPhone(data.family.primaryContact.phone || '');
      }
    }

    if (data.children && data.children.length > 0) {
      setChildren(data.children.map((child, idx) => ({
        id: idx + 1,
        name: child.name || '',
        dateOfBirth: child.dateOfBirth ? child.dateOfBirth.split('T')[0] : '',
        grade: '', // Don't prefill grade - user must select current grade
        previousGrade: child.previousGrade || null,
        catholicSchool: child.catholicSchool?.attends ? 'yes' : 'no',
        schoolName: child.catholicSchool?.schoolName || '',
        sacramentsReceived: child.sacraments?.received || [],
        sacramentsPreparing: [],
        previouslyPreparing: child.sacraments?.previouslyPreparing || [], // What they were preparing last year
        alreadyPaidSacramentFee: null, // Will ask if needed
        baptizedAtStBridget: child.baptism?.baptizedAtStBridget ? 'yes' : 'no',
        previouslyEnrolled: child.baptism?.previouslyEnrolled ? 'yes' : 'no',
        certificateFile: null,
        program: programs.length === 1 ? [programs[0].name.toLowerCase().replace(/\s+/g, '-')] : []
      })));
    }

    // Track previous registration payment status for sacrament fee logic
    if (data.previousRegistrationPaidInFull !== undefined) {
      setPreviousRegistrationPaidInFull(data.previousRegistrationPaidInFull);
    }

    if (data.permissions) {
      setPhotoPermission(data.permissions.photoPermission ? 'yes' : 'no');
    }
  };

  // Handle child data change
  const handleChildChange = (index, field, value) => {
    const newChildren = [...children];
    newChildren[index] = { ...newChildren[index], [field]: value };
    setChildren(newChildren);
  };

  // Handle checkbox array changes
  const handleChildCheckboxChange = (index, field, value, checked) => {
    const newChildren = [...children];
    const currentValues = newChildren[index][field] || [];
    if (checked) {
      newChildren[index][field] = [...currentValues, value];
    } else {
      newChildren[index][field] = currentValues.filter(v => v !== value);
    }
    setChildren(newChildren);
  };

  // Handle file upload
  const handleFileChange = (childIndex, file) => {
    setCertificateFiles({ ...certificateFiles, [childIndex]: file });
    handleChildChange(childIndex, 'certificateFile', file);
  };

  // Parent/Guardian management
  const handleParentChange = (index, field, value) => {
    const newParents = [...parents];
    newParents[index] = { ...newParents[index], [field]: value };
    setParents(newParents);
  };

  const addParent = () => {
    if (parents.length < 2) {
      setParents([...parents, { name: '', relationship: '', maidenName: '' }]);
    }
  };

  const removeParent = (index) => {
    if (parents.length > 1) {
      setParents(parents.filter((_, i) => i !== index));
    }
  };

  // Generate family name from parent names
  const generateFamilyName = () => {
    const parentNames = parents
      .filter(p => p.name)
      .map(p => {
        const nameParts = p.name.trim().split(' ');
        return nameParts[nameParts.length - 1]; // Get last name
      });

    if (parentNames.length === 0) return '';
    if (parentNames.length === 1) return `${parentNames[0]} Family`;

    // If same last name, use once; otherwise combine with /
    const uniqueNames = [...new Set(parentNames)];
    if (uniqueNames.length === 1) return `${uniqueNames[0]} Family`;
    return `${uniqueNames.join(' / ')} Family`;
  };

  // Calculate payment breakdown
  const calculateBreakdown = useCallback(() => {
    if (!settings) return { items: [], total: 0 };

    const items = [];
    let total = 0;
    let payingChildIndex = 0;

    const isDiscountActive = settings.discount?.enabled &&
      settings.discount?.endDate &&
      new Date() < new Date(settings.discount.endDate);

    const regFee = isDiscountActive
      ? (settings.discount.discountedRegistrationFee || 0)
      : (settings.registrationFee || 0);

    const addChildFee = isDiscountActive
      ? (settings.discount.discountedAdditionalChildFee || 0)
      : (settings.additionalChildFee || 0);

    const sacramentFee = settings.sacramentalPreparationFee || 0;

    children.forEach((child, idx) => {
      const childDisplay = child.name || t.childPlaceholder(idx + 1);
      // Skip if attends Catholic school
      if (child.catholicSchool === 'yes') {
        items.push({
          description: t.attendsCatholicSchool(childDisplay),
          amount: 0,
          note: t.noFeeRequired
        });
        return;
      }

      // First paying child gets full fee, additional children get additional fee
      const baseFee = payingChildIndex === 0 ? regFee : addChildFee;
      const feeLine = payingChildIndex === 0
        ? t.registrationFeeLine(childDisplay)
        : t.additionalChildFeeLine(childDisplay);

      items.push({
        description: feeLine,
        amount: baseFee,
        note: isDiscountActive ? t.earlyDiscountApplied : null
      });
      total += baseFee;
      payingChildIndex++;

      // Add sacrament fee if preparing for any
      if (child.sacramentsPreparing && child.sacramentsPreparing.length > 0) {
        const previouslyPreparing = child.previouslyPreparing || [];

        // Check each sacrament being prepared for
        child.sacramentsPreparing.forEach(sac => {
          const wasPreviouslyPreparing = previouslyPreparing.includes(sac);
          const sacLabel = t.sacramentLabels[sac] || sac;
          let shouldCharge = true;
          let feeNote = null;

          if (wasPreviouslyPreparing) {
            // This is year 2 of sacrament preparation
            if (previousRegistrationPaidInFull) {
              // Previous registration was paid in full - no charge for continuing preparation
              shouldCharge = false;
              feeNote = t.year2PreviouslyPaid;
            } else if (child.alreadyPaidSacramentFee === true) {
              // User confirmed they already paid the sacrament fee
              shouldCharge = false;
              feeNote = t.year2PreviouslyPaid;
            } else {
              // Previous registration not paid in full and user hasn't confirmed payment
              shouldCharge = true;
              feeNote = t.year2Outstanding;
            }
          }

          if (shouldCharge) {
            items.push({
              description: t.sacramentPrepLine(childDisplay, sacLabel),
              amount: sacramentFee,
              note: feeNote
            });
            total += sacramentFee;
          } else {
            items.push({
              description: t.sacramentPrepLine(childDisplay, sacLabel),
              amount: 0,
              note: feeNote
            });
          }
        });
      }
    });

    return { items, total };
  }, [settings, children, previousRegistrationPaidInFull, t]);

  // Calculate total (for backwards compatibility)
  const calculateTotal = useCallback(() => {
    return calculateBreakdown().total;
  }, [calculateBreakdown]);

  // Format phone number
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length > 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length > 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length > 0) {
      return `(${digits}`;
    }
    return '';
  };

  // Validation
  // Per-child validity check used both for the in-step "current child" gating
  // and the all-children gate on "Done Adding Children". Returns either true
  // when the child is fully valid, or an array of missing-field labels so the
  // caller can surface what's wrong and where to fix it.
  const getChildValidationIssues = (child, index) => {
    const issues = [];
    if (!child) return ['record missing'];
    if (!child.name) issues.push(t.validationIssues.name);
    if (!child.dateOfBirth) issues.push(t.validationIssues.dateOfBirth);
    if (!child.grade) issues.push(t.validationIssues.grade);
    if (child.catholicSchool === null || child.catholicSchool === '') issues.push(t.validationIssues.catholicSchool);
    if (programs.length > 1 && (!child.program || child.program.length === 0)) issues.push(t.validationIssues.program);
    const certificateNeeded =
      child.sacramentsReceived?.includes('baptism') &&
      child.baptizedAtStBridget === 'no' &&
      child.previouslyEnrolled === 'no';
    if (certificateNeeded && !certificateFiles[index] && !certificateAcknowledgments[index]) {
      issues.push(t.validationIssues.certificate);
    }
    return issues;
  };

  const isChildValid = (child, index) => getChildValidationIssues(child, index).length === 0;

  // Builds the [{index, label, issues[]}] of children with at least one issue —
  // used by the "Done Adding Children" gate and the helper alert above it.
  const getInvalidChildren = () => children
    .map((c, i) => ({ index: i, label: getChildDisplayName(c, i), issues: getChildValidationIssues(c, i) }))
    .filter(entry => entry.issues.length > 0);

  const validateCurrentStep = () => {
    switch (activeStep) {
      case 0: // Welcome
        if (isReturningFamily === 'yes' && !prefilledData && !verificationMessage.type === 'success') {
          return false;
        }
        return isReturningFamily !== null;

      case 1: // Parish registration
        return parishRegistered === 'yes';

      case 2: // Family info
        // At least one parent with name and relationship, plus contact info.
        // Email must be well-formed because the server forwards it to Square as
        // buyer_email_address; a malformed value triggers an INVALID_EMAIL_ADDRESS
        // rejection at payment time, which presents as a generic 402 to the user.
        const hasValidParent = parents.length > 0 &&
          parents[0].name &&
          parents[0].relationship;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return hasValidParent && contactName && contactPhone && contactEmail && emailRegex.test(contactEmail);

      case 3: // Children — gates intra-step navigation on the CURRENTLY-VIEWED child only.
        // Leaving the children step is gated separately (see "Done Adding Children" below)
        // because that has to verify every child, not just the one in view.
        return isChildValid(children[currentChildIndex], currentChildIndex);

      case 4: // Permissions
        return photoPermission !== null;

      case 5: // Signature
        return signature && signatureDate;

      case 6: // Payment
        if (paymentMethod === 'online') {
          return paymentAmount && parseInt(paymentAmount) >= 20;
        }
        return paymentMethod !== null;

      default:
        return true;
    }
  };

  // Display label for a child — first name when available, else "New Child" (first) / "Child #N" (additions)
  const getChildDisplayName = (child, index) => {
    const name = (child?.name || '').trim();
    if (name) {
      return name.split(/\s+/)[0];
    }
    return index === 0 ? t.newChild : t.childN(index + 1);
  };

  // Add another child
  const handleAddChild = () => {
    const newChild = createEmptyChild(children.length + 1);
    setChildren([...children, newChild]);
    setCurrentChildIndex(children.length); // Move to the new child
  };

  // Remove current child (if more than one)
  const handleRemoveChild = () => {
    if (children.length > 1) {
      const newChildren = children.filter((_, idx) => idx !== currentChildIndex);
      setChildren(newChildren);
      setCurrentChildIndex(Math.max(0, currentChildIndex - 1));
    }
  };

  // Done adding children - move to next step
  const handleDoneAddingChildren = () => {
    setActiveStep(activeStep + 1);
  };

  // Navigation
  const handleNext = () => {
    if (activeStep === 3) {
      // Children step - navigate through children first
      if (currentChildIndex < children.length - 1) {
        setCurrentChildIndex(currentChildIndex + 1);
        return;
      }
      // If on last child, don't auto-advance - let user choose to add more or finish
      return;
    }
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 3 && currentChildIndex > 0) {
      setCurrentChildIndex(currentChildIndex - 1);
      return;
    }
    setActiveStep(activeStep - 1);
  };

  // Submit form
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      let squarePaymentToken = null;

      // Process Square payment if online
      if (paymentMethod === 'online' && squareCard) {
        const result = await squareCard.tokenize();
        if (result.status === 'OK') {
          squarePaymentToken = result.token;
        } else {
          throw new Error(t.paymentFailed);
        }
      }

      // Prepare form data
      const formData = new FormData();

      const registrationData = {
        family: {
          name: generateFamilyName(), // Auto-generate from parent names
          parents: parents.filter(p => p.name && p.relationship).map(p => ({
            name: p.name,
            relationship: p.relationship,
            maidenName: p.maidenName || undefined
          })),
          primaryContact: {
            name: contactName,
            email: contactEmail,
            phone: contactPhone.replace(/\D/g, '')
          }
        },
        children: children.map(child => ({
          name: child.name,
          // Anchor the YYYY-MM-DD input at local midnight before sending. The raw
          // string would be parsed as midnight UTC, which renders as the previous
          // calendar day in any negative-offset timezone. Same approach the staff
          // form uses in ReligiousEducationForm.js so both flows store dates that
          // display correctly for parish staff in Eastern Time.
          dateOfBirth: (() => {
            if (!child.dateOfBirth) return child.dateOfBirth;
            const [year, month, day] = child.dateOfBirth.split('-');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toISOString();
          })(),
          grade: parseInt(child.grade),
          catholicSchool: {
            attends: child.catholicSchool === 'yes',
            schoolName: child.schoolName || ''
          },
          sacraments: {
            received: child.sacramentsReceived,
            preparing: child.sacramentsPreparing
          },
          baptism: {
            baptizedAtStBridget: child.baptizedAtStBridget === 'yes',
            previouslyEnrolled: child.previouslyEnrolled === 'yes'
          },
          program: child.program.length > 0 ? child.program :
            (programs.length === 1 ? [programs[0].name.toLowerCase().replace(/\s+/g, '-')] : [])
        })),
        permissions: {
          photoPermission: photoPermission === 'yes'
        },
        signature: {
          name: signature,
          date: signatureDate
        },
        payment: {
          amount: calculateTotal(),
          method: paymentMethod
        }
      };

      formData.append('family', JSON.stringify(registrationData.family));
      formData.append('children', JSON.stringify(registrationData.children));
      formData.append('permissions', JSON.stringify(registrationData.permissions));
      formData.append('signature', JSON.stringify(registrationData.signature));
      formData.append('payment', JSON.stringify(registrationData.payment));

      if (squarePaymentToken) {
        formData.append('squarePaymentToken', squarePaymentToken);
        formData.append('paymentAmount', paymentAmount);
        if (!paymentIdemKeyRef.current) {
          paymentIdemKeyRef.current = window.crypto?.randomUUID
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
        formData.append('paymentIdempotencyKey', paymentIdemKeyRef.current);
      }

      // Add certificate files
      Object.entries(certificateFiles).forEach(([index, file]) => {
        if (file) {
          formData.append(`baptismCertificates[${index}]`, file);
        }
      });

      await axios.post('/religious-education/public-submission', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
    } catch (err) {
      console.error('Submission error:', err);
      // 400/402 mean the server rejected before charging, so a retry is a new
      // payment attempt and needs a fresh key. Any other failure is ambiguous —
      // keep the key so a resubmit cannot double-charge.
      const status = err.response?.status;
      if (status === 400 || status === 402) {
        paymentIdemKeyRef.current = null;
      }
      setError(err.response?.data?.message || err.message || t.genericSubmitError);
    } finally {
      setSubmitting(false);
    }
  };

  // Language toggle chips, shown on every screen so users can switch at any point
  const LanguageToggle = () => (
    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
      <Chip
        label={t.languageLabel.en}
        size="small"
        color={language === 'en' ? 'primary' : 'default'}
        variant={language === 'en' ? 'filled' : 'outlined'}
        onClick={() => handleLanguageChange('en')}
      />
      <Chip
        label={t.languageLabel.es}
        size="small"
        color={language === 'es' ? 'primary' : 'default'}
        variant={language === 'es' ? 'filled' : 'outlined'}
        onClick={() => handleLanguageChange('es')}
      />
    </Stack>
  );

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 2, px: isMobile ? 1 : 2, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t.loading}</Typography>
      </Container>
    );
  }

  // Registration closed
  if (!registrationEnabled) {
    return (
      <Container maxWidth="md" sx={{ mt: 2, px: isMobile ? 1 : 2 }}>
        <Paper sx={{ p: isMobile ? 2 : 4 }}>
          <LanguageToggle />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom>{t.closedTitle}</Typography>
            <Typography>{t.closedBody}</Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Success state
  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 2, px: isMobile ? 1 : 2 }}>
        <Paper sx={{ p: isMobile ? 2 : 4 }}>
          <LanguageToggle />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant={isMobile ? 'h5' : 'h4'} color="success.main" gutterBottom>
              {t.successTitle}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t.successBody}
            </Typography>
            {paymentMethod === 'cash' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t.successCashReminder}
              </Alert>
            )}
          </Box>
        </Paper>
      </Container>
    );
  }

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Welcome / Returning Family
        return (
          <Box>
            <Typography variant="h5" gutterBottom>{t.welcomeTitle}</Typography>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel>{t.returningQuestion}</FormLabel>
              <RadioGroup
                value={isReturningFamily || ''}
                onChange={(e) => setIsReturningFamily(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label={t.returningYes} />
                <FormControlLabel value="no" control={<Radio />} label={t.returningNo} />
              </RadioGroup>
            </FormControl>

            {isReturningFamily === 'yes' && (
              <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography gutterBottom>
                  {t.returningEmailBlurb}
                </Typography>
                <TextField
                  fullWidth
                  label={t.emailAddress}
                  type="email"
                  value={returningEmail}
                  onChange={(e) => setReturningEmail(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button variant="outlined" onClick={handleSendVerificationCode} sx={{ mb: 2 }}>
                  {t.sendVerificationCode}
                </Button>

                {verificationSent && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label={t.verificationCode}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <Button variant="contained" onClick={handleVerifyCode}>
                      {t.verifyAndPrefill}
                    </Button>
                  </Box>
                )}

                {verificationMessage.text && (
                  <Alert severity={verificationMessage.type === 'error' ? 'error' : 'success'} sx={{ mt: 2 }}>
                    {verificationMessage.text}
                  </Alert>
                )}

                <Typography sx={{ mt: 2 }}>
                  <Link href="#" onClick={(e) => { e.preventDefault(); setIsReturningFamily('no'); }}>
                    {t.skipAndStartFresh}
                  </Link>
                </Typography>
              </Paper>
            )}
          </Box>
        );

      case 1: // Parish Registration
        return (
          <Box>
            <Typography variant="h5" gutterBottom>{t.parishStepTitle}</Typography>
            <FormControl component="fieldset">
              <FormLabel>{t.parishQuestion}</FormLabel>
              <RadioGroup
                value={parishRegistered || ''}
                onChange={(e) => setParishRegistered(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label={t.yes} />
                <FormControlLabel value="no" control={<Radio />} label={t.no} />
              </RadioGroup>
            </FormControl>

            {parishRegistered === 'no' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t.parishNotRegisteredBefore}
                <Link href="https://www.stbridgetup.org/register" target="_blank">{t.parishNotRegisteredLink}</Link>
                {t.parishNotRegisteredAfter}
              </Alert>
            )}
          </Box>
        );

      case 2: // Family Information
        return (
          <Box>
            <Typography variant="h5" gutterBottom>{t.familyStepTitle}</Typography>

            {/* Parents/Guardians Section */}
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>{t.parentsGuardians}</Typography>

            {parents.map((parent, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {index === 0 ? t.parentGuardian1 : t.parentGuardian2}
                  </Typography>
                  {index > 0 && (
                    <Button size="small" color="error" onClick={() => removeParent(index)}>
                      {t.remove}
                    </Button>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t.fullName}
                      value={parent.name}
                      onChange={(e) => handleParentChange(index, 'name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>{t.relationship}</InputLabel>
                      <Select
                        value={parent.relationship}
                        label={t.relationship}
                        onChange={(e) => handleParentChange(index, 'relationship', e.target.value)}
                      >
                        {relationshipOptions.map(rel => (
                          <MenuItem key={rel} value={rel}>{t.relationshipOptions[rel] || rel}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {parent.relationship === 'Mother' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t.maidenName}
                        value={parent.maidenName}
                        onChange={(e) => handleParentChange(index, 'maidenName', e.target.value)}
                        helperText={t.ifApplicable}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}

            {parents.length < 2 && (
              <Button
                variant="outlined"
                onClick={addParent}
                sx={{ mb: 3 }}
                fullWidth={isMobile}
              >
                {t.addSecondParent}
              </Button>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Primary Contact Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>{t.primaryContact}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t.primaryContactBlurb}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t.contactName}
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t.phone}
                  value={contactPhone}
                  onChange={(e) => setContactPhone(formatPhone(e.target.value))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t.email}
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                  error={contactEmail.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)}
                  helperText={
                    contactEmail.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)
                      ? t.invalidEmailHelper
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3: // Children
        const child = children[currentChildIndex];
        const currentChildLabel = getChildDisplayName(child, currentChildIndex);
        return (
          <Box>
            {/* Child selector chips — visible when more than one child has been added */}
            {children.length > 1 && (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ mb: 2 }}
              >
                {children.map((c, idx) => (
                  <Chip
                    key={idx}
                    label={getChildDisplayName(c, idx)}
                    color={idx === currentChildIndex ? 'primary' : 'default'}
                    variant={idx === currentChildIndex ? 'filled' : 'outlined'}
                    onClick={() => setCurrentChildIndex(idx)}
                    sx={{
                      height: isMobile ? 40 : undefined,
                      fontSize: isMobile ? '0.95rem' : undefined,
                      px: isMobile ? 0.5 : undefined
                    }}
                  />
                ))}
              </Stack>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: isMobile ? 1 : 0,
                mb: 2
              }}
            >
              <Typography variant="h5">
                {currentChildLabel}{(child?.name || '').trim() ? t.childInformationSuffix : ''}
              </Typography>
              {children.length > 1 && (
                <Button
                  size="small"
                  color="error"
                  onClick={handleRemoveChild}
                  sx={{ alignSelf: isMobile ? 'flex-end' : 'auto' }}
                >
                  {t.removeThisChild}
                </Button>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t.childsName}
                  value={child.name}
                  onChange={(e) => handleChildChange(currentChildIndex, 'name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label={t.dateOfBirth}
                  value={child.dateOfBirth}
                  onChange={(e) => handleChildChange(currentChildIndex, 'dateOfBirth', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{settings?.currentSchoolYear ? t.gradeWithYear(settings.currentSchoolYear) : t.grade}</InputLabel>
                  <Select
                    value={child.grade}
                    label={settings?.currentSchoolYear ? t.gradeWithYear(settings.currentSchoolYear) : t.grade}
                    onChange={(e) => handleChildChange(currentChildIndex, 'grade', e.target.value)}
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(g => (
                      <MenuItem key={g} value={g}>{g}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {child.previousGrade && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {t.lastYearGrade(child.previousGrade)}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel>{t.catholicSchoolQuestion}</FormLabel>
                  <RadioGroup
                    row={!isMobile}
                    value={child.catholicSchool || ''}
                    onChange={(e) => handleChildChange(currentChildIndex, 'catholicSchool', e.target.value)}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label={t.yes} />
                    <FormControlLabel value="no" control={<Radio />} label={t.no} />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {child.catholicSchool === 'yes' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t.schoolName}
                    value={child.schoolName}
                    onChange={(e) => handleChildChange(currentChildIndex, 'schoolName', e.target.value)}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel>{t.sacramentsReceivedQuestion}</FormLabel>
                  <FormGroup row={!isMobile}>
                    {['baptism', 'communion', 'confirmation'].map(sac => (
                      <FormControlLabel
                        key={sac}
                        control={
                          <Checkbox
                            checked={child.sacramentsReceived.includes(sac)}
                            onChange={(e) => handleChildCheckboxChange(currentChildIndex, 'sacramentsReceived', sac, e.target.checked)}
                          />
                        }
                        label={t.sacramentLabels[sac]}
                        sx={{ minHeight: isMobile ? 44 : undefined }}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel>{t.sacramentsPreparingQuestion}</FormLabel>
                  <FormGroup row={!isMobile}>
                    {['baptism', 'communion', 'confirmation'].map(sac => {
                      const alreadyReceived = child.sacramentsReceived.includes(sac);
                      return (
                        <FormControlLabel
                          key={sac}
                          control={
                            <Checkbox
                              checked={child.sacramentsPreparing.includes(sac)}
                              onChange={(e) => handleChildCheckboxChange(currentChildIndex, 'sacramentsPreparing', sac, e.target.checked)}
                              disabled={alreadyReceived}
                            />
                          }
                          label={t.sacramentLabels[sac]}
                          sx={{
                            minHeight: isMobile ? 44 : undefined,
                            opacity: alreadyReceived ? 0.5 : 1
                          }}
                        />
                      );
                    })}
                  </FormGroup>
                </FormControl>
              </Grid>

              {/* Ask about prior sacrament fee payment for returning families with unpaid balance */}
              {child.previouslyPreparing &&
               child.previouslyPreparing.length > 0 &&
               child.sacramentsPreparing.some(sac => child.previouslyPreparing.includes(sac)) &&
               previousRegistrationPaidInFull === false && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>{t.continuingSacPrepTitle}</strong>
                    </Typography>
                    <Typography variant="body2">
                      {t.continuingSacPrepBody(
                        child.name || t.yourChild,
                        child.previouslyPreparing.map(s => t.sacramentLabels[s] || s).join(t.sacramentJoiner)
                      )}
                    </Typography>
                  </Alert>
                  <FormControl component="fieldset">
                    <FormLabel>{t.paidSacramentFeeQuestion}</FormLabel>
                    <RadioGroup
                      value={child.alreadyPaidSacramentFee === true ? 'yes' : child.alreadyPaidSacramentFee === false ? 'no' : ''}
                      onChange={(e) => handleChildChange(currentChildIndex, 'alreadyPaidSacramentFee', e.target.value === 'yes')}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label={t.yesAlreadyPaid} />
                      <FormControlLabel value="no" control={<Radio />} label={t.noDidNotPay} />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}

              {child.sacramentsReceived.includes('baptism') && (
                <>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel>{t.baptizedAtStBridgetQuestion}</FormLabel>
                      <RadioGroup
                        row={!isMobile}
                        value={child.baptizedAtStBridget || ''}
                        onChange={(e) => handleChildChange(currentChildIndex, 'baptizedAtStBridget', e.target.value)}
                      >
                        <FormControlLabel value="yes" control={<Radio />} label={t.yes} />
                        <FormControlLabel value="no" control={<Radio />} label={t.no} />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {child.baptizedAtStBridget === 'no' && (
                    <>
                      <Grid item xs={12}>
                        <FormControl component="fieldset">
                          <FormLabel>{t.previouslyEnrolledQuestion}</FormLabel>
                          <RadioGroup
                            row={!isMobile}
                            value={child.previouslyEnrolled || ''}
                            onChange={(e) => handleChildChange(currentChildIndex, 'previouslyEnrolled', e.target.value)}
                          >
                            <FormControlLabel value="yes" control={<Radio />} label={t.yes} />
                            <FormControlLabel value="no" control={<Radio />} label={t.no} />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {child.previouslyEnrolled === 'no' && (
                        <Grid item xs={12}>
                          <Typography variant="body2" gutterBottom>
                            {t.uploadCertificatePrompt}
                          </Typography>
                          <Button
                            variant="outlined"
                            component="label"
                            fullWidth={isMobile}
                            sx={{ minHeight: isMobile ? 48 : undefined }}
                          >
                            {certificateFiles[currentChildIndex]
                              ? certificateFiles[currentChildIndex].name
                              : t.chooseFile}
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleFileChange(currentChildIndex, e.target.files[0])}
                            />
                          </Button>

                          {!certificateFiles[currentChildIndex] && (
                            <FormControlLabel
                              sx={{ mt: 2, alignItems: 'flex-start' }}
                              control={
                                <Checkbox
                                  checked={Boolean(certificateAcknowledgments[currentChildIndex])}
                                  onChange={(e) =>
                                    setCertificateAcknowledgments(prev => ({
                                      ...prev,
                                      [currentChildIndex]: e.target.checked
                                    }))
                                  }
                                  sx={{ pt: 0 }}
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  {t.certificateAcknowledgment}
                                </Typography>
                              }
                            />
                          )}
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Program Selection */}
              {programs.length > 1 && (
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel>{t.programSelection}</FormLabel>
                    <FormGroup>
                      {programs.map(program => {
                        const programValue = program.name.toLowerCase().replace(/\s+/g, '-');
                        return (
                          <FormControlLabel
                            key={program._id}
                            control={
                              <Checkbox
                                checked={child.program.includes(programValue)}
                                onChange={(e) => handleChildCheckboxChange(currentChildIndex, 'program', programValue, e.target.checked)}
                              />
                            }
                            label={`${program.name}${program.description ? ': ' + program.description : ''}`}
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>
                </Grid>
              )}

              {programs.length === 1 && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    {t.willBeEnrolledInPrefix}<strong>{programs[0].name}</strong>
                  </Alert>
                </Grid>
              )}

              {/* Add Another Child / Done buttons */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />

                {/* Surface any sibling-child issues so the user isn't stuck wondering
                    why "Done Adding Children" is disabled. Each entry is a button
                    that jumps to that child so they can fix it. Shown only when at
                    least one OTHER child has problems; the current-child issues are
                    already visible to the user via the form fields themselves. */}
                {(() => {
                  const invalid = getInvalidChildren().filter(e => e.index !== currentChildIndex);
                  if (invalid.length === 0) return null;
                  return (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t.invalidChildrenHeader}
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {invalid.map(entry => (
                          <Button
                            key={entry.index}
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => setCurrentChildIndex(entry.index)}
                          >
                            {entry.label}: {entry.issues.join(', ')}
                          </Button>
                        ))}
                      </Stack>
                    </Alert>
                  );
                })()}

                <Box sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: 2,
                  justifyContent: 'center'
                }}>
                  <Button
                    variant="outlined"
                    onClick={handleAddChild}
                    fullWidth={isMobile}
                    sx={{ minHeight: isMobile ? 48 : undefined }}
                  >
                    {t.addAnotherChild}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleDoneAddingChildren}
                    disabled={getInvalidChildren().length > 0}
                    fullWidth={isMobile}
                    sx={{ minHeight: isMobile ? 48 : undefined }}
                  >
                    {t.doneAddingChildren}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );

      case 4: // Permissions
        return (
          <Box>
            <Typography variant="h5" gutterBottom>{t.permissionsStepTitle}</Typography>
            <FormControl component="fieldset">
              <FormLabel>{t.photoPermissionLabel}</FormLabel>
              <Typography variant="body2" sx={{ my: 2, color: 'text.secondary' }}>
                {t.photoPermissionBody}
              </Typography>
              <RadioGroup
                value={photoPermission || ''}
                onChange={(e) => setPhotoPermission(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label={t.grantPermission} />
                <FormControlLabel value="no" control={<Radio />} label={t.denyPermission} />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 5: // Signature
        return (
          <Box>
            <Typography variant="h5" gutterBottom>{t.signatureStepTitle}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t.parentGuardianSignature}
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  required
                  helperText={t.typeYourFullName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label={t.date}
                  value={signatureDate}
                  onChange={(e) => setSignatureDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 6: // Payment
        const breakdown = calculateBreakdown();
        const total = breakdown.total;
        return (
          <Box>
            <Typography variant="h5" gutterBottom>{t.paymentStepTitle}</Typography>

            {/* Payment Breakdown */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t.registrationSummary}
              </Typography>

              {breakdown.items.map((item, idx) => (
                <Box key={idx} sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  py: 1,
                  borderBottom: idx < breakdown.items.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider'
                }}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography variant="body2">
                      {item.description}
                    </Typography>
                    {item.note && (
                      <Typography variant="caption" color="success.main">
                        {item.note}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight="medium">
                    {item.amount === 0 ? t.free : `$${item.amount.toFixed(2)}`}
                  </Typography>
                </Box>
              ))}

              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                mt: 1,
                borderTop: '2px solid',
                borderColor: 'primary.main'
              }}>
                <Typography variant="h6">{t.totalDue}</Typography>
                <Typography variant="h6" color="primary.main">
                  ${total.toFixed(2)}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t.freeFormationNote}
              </Typography>
            </Paper>

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel>{t.paymentMethod}</FormLabel>
              <RadioGroup
                value={paymentMethod || ''}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <FormControlLabel value="online" control={<Radio />} label={t.payOnline} />
                <FormControlLabel value="cash" control={<Radio />} label={t.payInPerson} />
              </RadioGroup>
            </FormControl>

            {paymentMethod === 'online' && (
              <Box>
                <TextField
                  type="number"
                  label={t.paymentAmount}
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  inputProps={{ min: 20, max: total }}
                  helperText={t.paymentAmountHelper(total)}
                  sx={{ mb: 2, width: isMobile ? '100%' : 200 }}
                  fullWidth={isMobile}
                />
                <Box id="card-container" sx={{ mt: 2, mb: 2 }}></Box>
                <Typography id="card-errors" color="error"></Typography>
              </Box>
            )}

            {paymentMethod === 'cash' && (
              <Alert severity="info">
                {t.cashInstructions}
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: isMobile ? 1 : 4, mb: 4, px: isMobile ? 1 : 2 }}>
      <Paper sx={{ p: isMobile ? 2 : 4 }}>
        <LanguageToggle />
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          align="center"
          gutterBottom
          sx={{ fontSize: isMobile ? '1.25rem' : undefined }}
        >
          {t.header}
        </Typography>

        {/* Desktop Stepper */}
        {!isMobile && (
          <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Mobile Progress Indicator */}
        {isMobile && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
              {t.stepOfTotal(activeStep + 1, steps.length)}
            </Typography>
            <Typography variant="subtitle1" align="center" fontWeight="medium">
              {steps[activeStep]}
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 4,
                bgcolor: 'grey.200',
                borderRadius: 2,
                mt: 1,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  width: `${((activeStep + 1) / steps.length) * 100}%`,
                  height: '100%',
                  bgcolor: 'primary.main',
                  borderRadius: 2,
                  transition: 'width 0.3s ease'
                }}
              />
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}

        {/* Hide divider and nav buttons on children step - it has its own buttons */}
        {activeStep !== 3 && (
          <>
            <Divider sx={{ my: 3 }} />

            {/* Navigation Buttons */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: isMobile ? 'column-reverse' : 'row',
              gap: isMobile ? 2 : 0
            }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                size={isMobile ? 'large' : 'medium'}
                fullWidth={isMobile}
                startIcon={isMobile ? <KeyboardArrowLeft /> : null}
                sx={{ minHeight: isMobile ? 48 : undefined }}
              >
                {t.back}
              </Button>
              <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!validateCurrentStep() || submitting}
                    size={isMobile ? 'large' : 'medium'}
                    fullWidth={isMobile}
                    sx={{ minHeight: isMobile ? 48 : undefined }}
                  >
                    {submitting ? <CircularProgress size={24} /> : t.submit}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!validateCurrentStep()}
                    size={isMobile ? 'large' : 'medium'}
                    fullWidth={isMobile}
                    endIcon={isMobile ? <KeyboardArrowRight /> : null}
                    sx={{ minHeight: isMobile ? 48 : undefined }}
                  >
                    {t.next}
                  </Button>
                )}
              </Box>
            </Box>
          </>
        )}

        {/* Children-step navigation: Previous/Next child (or back to prior step on first child) */}
        {activeStep === 3 && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 1,
              justifyContent: 'space-between'
            }}
          >
            {currentChildIndex > 0 ? (
              <Button
                onClick={() => setCurrentChildIndex(currentChildIndex - 1)}
                size={isMobile ? 'large' : 'medium'}
                fullWidth={isMobile}
                startIcon={<KeyboardArrowLeft />}
                sx={{ minHeight: isMobile ? 48 : undefined }}
              >
                {t.previousChild(getChildDisplayName(children[currentChildIndex - 1], currentChildIndex - 1))}
              </Button>
            ) : (
              <Button
                onClick={() => setActiveStep(activeStep - 1)}
                size={isMobile ? 'large' : 'medium'}
                fullWidth={isMobile}
                startIcon={<KeyboardArrowLeft />}
                sx={{ minHeight: isMobile ? 48 : undefined }}
              >
                {t.back}
              </Button>
            )}

            {currentChildIndex < children.length - 1 && (
              <Button
                onClick={() => setCurrentChildIndex(currentChildIndex + 1)}
                size={isMobile ? 'large' : 'medium'}
                fullWidth={isMobile}
                endIcon={<KeyboardArrowRight />}
                sx={{ minHeight: isMobile ? 48 : undefined }}
              >
                {t.nextChild(getChildDisplayName(children[currentChildIndex + 1], currentChildIndex + 1))}
              </Button>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default PublicRegistration;
